import * as React from "react";
import { Employee, EmployeeLineItem } from "@interfaces/employee";
import { decodeEmployee, encodeEmployee } from "decoders/employee";
import {
  deleteBatchApiData,
  getApiData,
  listApiData,
  postApiData,
  postBatchApiData,
} from "@hooks/utils/api";

export const useEmployee = (organisationId?: string) => {
  const [employees, setEmployees] = React.useState<EmployeeLineItem[]>([]);
  const [isEmployeeLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getEmployee = async (
    id: string
  ): Promise<EmployeeLineItem | undefined> => {
    try {
      setIsLoading(true);
      const employeeResult = await getApiData(
        "getContractorEmployee",
        "contractorEmployee",
        id
      );
      if (Object.keys(employeeResult.data).length === 0) {
        return undefined;
      }
      return employeeResult
        ? decodeEmployee(employeeResult.data as Employee)
        : undefined;
    } catch (e: any) {
      setError("Could not fetch employee");
    } finally {
      setIsLoading(false);
    }
  };

  const listEmployees = async (organisationId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const employeeResult = await listApiData(
        "listContractorEmployees",
        "contractorEmployee",
        organisationId
      );
      setEmployees(
        employeeResult.data.map((p) => decodeEmployee(p as Employee))
      );
    } catch (e: any) {
      setError("Could not list employees");
    } finally {
      setIsLoading(false);
    }
  };

  const searchEmployees = async (
    query: string
  ): Promise<EmployeeLineItem[]> => {
    try {
      const employeeResult = await listApiData(
        "searchContractorEmployees",
        "contractorEmployee",
        query
      );
      return employeeResult.data.map((p) => decodeEmployee(p as Employee));
    } catch (e: any) {
      setError("Could not search employees");
      return [];
    }
  };

  const createEmployee = async (
    employee: EmployeeLineItem
  ): Promise<EmployeeLineItem> => {
    try {
      setIsLoading(true);
      const encodedEmployee = encodeEmployee(employee);
      const upsertedEmployee = await postApiData(
        "createContractorEmployee",
        "contractorEmployee",
        encodedEmployee
      );
      const decodedEmployee = decodeEmployee(upsertedEmployee.data as Employee);
      setEmployees([...employees, decodedEmployee]);
      return decodedEmployee;
    } catch (e: any) {
      setError("Could not list employees");
    } finally {
      setIsLoading(false);
    }
    return employee;
  };

  const updateEmployee = async (employee: EmployeeLineItem): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedEmployee = encodeEmployee(employee);
      await postApiData(
        "updateContractorEmployee",
        "contractorEmployee",
        encodedEmployee
      );
      setEmployees(
        employees.map((c) => {
          if (c.id === employee.id) {
            return employee;
          }
          return c;
        })
      );
    } catch (e: any) {
      setError("Could not update employee");
    } finally {
      setIsLoading(false);
    }
  };

  const upsertEmployees = async (
    employeesToUpsert: EmployeeLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedEmployees = employeesToUpsert.map((e) => encodeEmployee(e));
      await postBatchApiData(
        "upsertContractorEmployees",
        "contractorEmployee",
        encodedEmployees
      );

      const updatedEmployees = employees.reduce((acc, employee) => {
        const updatedEmployee = employeesToUpsert.find(
          (e) => e.id === employee.id
        );
        if (updatedEmployee) {
          return [...acc, updatedEmployee];
        }
        return [...acc, employee];
      }, [] as EmployeeLineItem[]);

      const createdEmployees = employeesToUpsert.filter((employee) => {
        return !updatedEmployees.find((e) => e.id === employee.id);
      });

      setEmployees([...updatedEmployees, ...createdEmployees]);
    } catch (e: any) {
      setError("Could not update employee");
    } finally {
      setIsLoading(false);
    }
  };
  const deleteEmployees = async (
    employeesToDelete: EmployeeLineItem[]
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const encodedEmployee = employeesToDelete.map((e) => encodeEmployee(e));
      await deleteBatchApiData(
        "deleteContractorEmployees",
        "contractorEmployee",
        encodedEmployee
      );

      const deletedRowIds = employeesToDelete.map((e) => e.id);
      setEmployees(
        employees.filter((e) => {
          return !deletedRowIds.includes(e.id);
        })
      );
    } catch (e: any) {
      setError("Could not delete employee");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (organisationId) {
      listEmployees(organisationId);
    }
  }, [organisationId]);

  return {
    employees,
    getEmployee,
    listEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployees,
    upsertEmployees,
    searchEmployees,
    isEmployeeLoading,
    error,
  };
};
